import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import axiosInstance from "../../utils/axios";

const STATUS_OPTIONS = [
  "Received", "Contacted - Waiting Patient Signature", "Ready to Bill",
  "Ready to Ship", "Shipped", "DENIED-READ DETAILS", "Completed",
  "Re-Ship Item", "Need Additional Information", "Medical Records Requested",
  "VOYAGE COMPLETED", "Collect CashPay", "Collect Deposit",
  "Under Review - On Hold", "Check Insurance", "Call Patient",
  "Checking Eligibility", "LVM", "Collect Copay/Coinsurance",
];

const MEDICAL_RECORDS_OPTIONS = [
  "Medical Records Completed", "Medical Records Needed", "Medical Records Requested",
];

const SIZE_OPTIONS = [
  "I", "I OT", "II", "II OT", "III", "III OT", "IV", "IV OT",
  "V", "V OT", "MOTIF S", "MOTIF M", "MOTIF L", "MOTIF XL", "Wrap-S",
];

const STATUS_COLORS = {
  "Completed":                         { bg: "#EBF5E7", color: "#2D6A1F", border: "#7BBF6A" },
  "Shipped":                           { bg: "#EBF5E7", color: "#2D6A1F", border: "#7BBF6A" },
  "Ready to Ship":                     { bg: "#E8F4F7", color: "#2A6070", border: "#7BBCC8" },
  "Ready to Bill":                     { bg: "#E8F4F7", color: "#2A6070", border: "#7BBCC8" },
  "VOYAGE COMPLETED":                  { bg: "#EBF5E7", color: "#2D6A1F", border: "#7BBF6A" },
  "DENIED-READ DETAILS":               { bg: "#FEE2E2", color: "#991B1B", border: "#FCA5A5" },
  "Need Additional Information":       { bg: "#FEF9C3", color: "#854D0E", border: "#FDE047" },
  "Under Review - On Hold":            { bg: "#FEF9C3", color: "#854D0E", border: "#FDE047" },
  "Check Insurance":                   { bg: "#FEF9C3", color: "#854D0E", border: "#FDE047" },
  "Checking Eligibility":              { bg: "#FEF9C3", color: "#854D0E", border: "#FDE047" },
  "Call Patient":                      { bg: "#FEF9C3", color: "#854D0E", border: "#FDE047" },
  "Collect CashPay":                   { bg: "#FBF5E8", color: "#7A5000", border: "#C9A96E" },
  "Collect Deposit":                   { bg: "#FBF5E8", color: "#7A5000", border: "#C9A96E" },
  "Collect Copay/Coinsurance":         { bg: "#FBF5E8", color: "#7A5000", border: "#C9A96E" },
  "Received":                          { bg: "#F0F4FF", color: "#3730A3", border: "#A5B4FC" },
  "Contacted - Waiting Patient Signature": { bg: "#F0F4FF", color: "#3730A3", border: "#A5B4FC" },
  "Medical Records Requested":         { bg: "#F0F4FF", color: "#3730A3", border: "#A5B4FC" },
  "LVM":                               { bg: "#F3F4F6", color: "#374151", border: "#9CA3AF" },
  "Re-Ship Item":                      { bg: "#F3F4F6", color: "#374151", border: "#9CA3AF" },
};

const StatusBadge = ({ value }) => {
  const style = STATUS_COLORS[value] || { bg: "#F3F4F6", color: "#374151", border: "#9CA3AF" };
  return (
    <span style={{
      background: style.bg, color: style.color,
      border: `1px solid ${style.border}`,
      borderRadius: "20px", padding: "3px 10px",
      fontSize: "10px", fontWeight: 500, letterSpacing: "0.3px",
      whiteSpace: "nowrap", fontFamily: "'Syne', sans-serif",
    }}>
      {value || "—"}
    </span>
  );
};

const ohStyles = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=Syne:wght@300;400;500;600&display=swap');

.oh-root *{box-sizing:border-box;margin:0;padding:0}
.oh-root{font-family:'Syne',sans-serif;color:#1E2A3A;width:100%}

/* CHIPS */
.oh-chips{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:1.5rem}
.oh-chip{
  display:flex;align-items:center;gap:6px;
  background:white;border:1px solid rgba(30,42,58,0.1);
  border-radius:20px;padding:5px 12px;font-size:11px;
  font-family:'Syne',sans-serif;color:#6B7A8D;
  box-shadow:0 1px 4px rgba(30,42,58,0.05);
}
.oh-chip strong{color:#1E2A3A;font-weight:600}
.oh-chip.highlight{border-color:#4E8A3C;background:#EBF5E7}
.oh-chip.highlight strong{color:#4E8A3C}

/* FILTER BAR */
.oh-filters{
  display:flex;align-items:center;gap:10px;flex-wrap:wrap;
  background:white;border:1px solid rgba(30,42,58,0.1);
  border-radius:10px;padding:1rem 1.25rem;
  margin-bottom:1.5rem;
  box-shadow:0 2px 8px rgba(30,42,58,0.05);
}
.oh-search{
  flex:1;min-width:180px;background:#F7F6F2;
  border:1px solid rgba(30,42,58,0.1);border-radius:6px;
  padding:0.6rem 1rem;font-size:13px;font-family:'Syne',sans-serif;
  color:#1E2A3A;outline:none;transition:border-color 0.2s;
}
.oh-search:focus{border-color:#4E8A3C;background:white}
.oh-search::placeholder{color:rgba(30,42,58,0.3)}
.oh-filter-select{
  background:#F7F6F2;border:1px solid rgba(30,42,58,0.1);
  border-radius:6px;padding:0.6rem 0.9rem;font-size:12px;
  font-family:'Syne',sans-serif;color:#1E2A3A;
  outline:none;cursor:pointer;transition:border-color 0.2s;
  -webkit-appearance:none;
}
.oh-filter-select:focus{border-color:#4E8A3C}
.oh-filter-date{
  background:#F7F6F2;border:1px solid rgba(30,42,58,0.1);
  border-radius:6px;padding:0.6rem 0.9rem;font-size:12px;
  font-family:'Syne',sans-serif;color:#1E2A3A;outline:none;
}
.oh-filter-date:focus{border-color:#4E8A3C;outline:none}
.oh-filter-label{font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:#6B7A8D;font-weight:500;white-space:nowrap}
.oh-filter-group{display:flex;flex-direction:column;gap:4px}
.oh-clear-btn{
  background:transparent;border:1px solid rgba(30,42,58,0.12);
  border-radius:6px;padding:0.6rem 1rem;font-size:11px;
  font-family:'Syne',sans-serif;color:#6B7A8D;cursor:pointer;
  transition:all 0.2s;white-space:nowrap;
}
.oh-clear-btn:hover{border-color:#1E2A3A;color:#1E2A3A}

/* TABLE */
.oh-table-wrap{background:white;border:1px solid rgba(30,42,58,0.1);border-radius:10px;overflow-x:auto;overflow-y:hidden;box-shadow:0 2px 12px rgba(30,42,58,0.05)}
.oh-table{width:100%;border-collapse:collapse;min-width:1200px;}
.oh-table thead{background:#F7F6F2;border-bottom:1px solid rgba(30,42,58,0.1)}
.oh-table th{
  padding:0.75rem 1rem;text-align:left;
  font-size:10px;letter-spacing:1.5px;text-transform:uppercase;
  color:#6B7A8D;font-weight:500;white-space:nowrap;cursor:pointer;
  user-select:none;transition:color 0.15s;
}
.oh-table th:hover{color:#1E2A3A}
.oh-table th.sorted{color:#4E8A3C}
.oh-table td{
  padding:0.85rem 1rem;font-size:12px;color:#1E2A3A;
  border-bottom:1px solid rgba(30,42,58,0.06);
  vertical-align:middle;
  max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
}
.oh-table tr:last-child td{border-bottom:none}
.oh-table tr:hover td{background:#F7F6F2}
.oh-table td.muted{color:#6B7A8D;font-weight:300}
.oh-table td.truncate{
  max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
  cursor:default;
}
/* EDIT BUTTON */
.oh-edit-btn{
  background:transparent;border:1px solid rgba(78,138,60,0.2);
  border-radius:5px;padding:4px 10px;font-size:11px;
  font-family:'Syne',sans-serif;color:#4E8A3C;cursor:pointer;
  transition:all 0.2s;
}
.oh-edit-btn:hover{background:#EBF5E7;border-color:#4E8A3C}

/* PAGINATION */
.oh-pagination{display:flex;align-items:center;justify-content:space-between;padding:1rem 1.25rem;border-top:1px solid rgba(30,42,58,0.08)}
.oh-page-info{font-size:12px;color:#6B7A8D}
.oh-page-btns{display:flex;gap:6px}
.oh-page-btn{
  background:white;border:1px solid rgba(30,42,58,0.12);
  border-radius:5px;padding:5px 12px;font-size:12px;
  font-family:'Syne',sans-serif;color:#1E2A3A;cursor:pointer;
  transition:all 0.2s;
}
.oh-page-btn:hover{border-color:#4E8A3C;color:#4E8A3C}
.oh-page-btn:disabled{opacity:0.4;cursor:not-allowed}
.oh-page-btn.active{background:#4E8A3C;color:white;border-color:#4E8A3C}

/* EDIT MODAL */
.oh-modal-overlay{position:fixed;inset:0;background:rgba(30,42,58,0.5);backdrop-filter:blur(4px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:1rem}
.oh-modal{background:white;border-radius:12px;overflow:hidden;width:100%;max-width:680px;max-height:90vh;overflow-y:auto;box-shadow:0 24px 60px rgba(30,42,58,0.2)}
.oh-modal-band{height:3px;background:linear-gradient(90deg,#4E8A3C,#7BBCC8)}
.oh-modal-header{padding:1.5rem 1.75rem;border-bottom:1px solid rgba(30,42,58,0.08);display:flex;align-items:center;justify-content:space-between}
.oh-modal-title{font-family:'Playfair Display',serif;font-size:20px;color:#1E2A3A;font-weight:400}
.oh-modal-close{width:28px;height:28px;border-radius:6px;background:rgba(30,42,58,0.05);border:1px solid rgba(30,42,58,0.1);display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:16px;color:#6B7A8D;transition:all 0.2s}
.oh-modal-close:hover{background:rgba(30,42,58,0.1)}
.oh-modal-body{padding:1.75rem}
.oh-modal-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:1rem}
.oh-modal-field{display:flex;flex-direction:column;gap:0.35rem}
.oh-modal-label{font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:#6B7A8D;font-weight:500}
.oh-modal-input,.oh-modal-select{
  background:#F7F6F2;border:1px solid rgba(30,42,58,0.1);
  border-radius:6px;padding:0.65rem 0.9rem;font-size:13px;
  font-family:'Syne',sans-serif;color:#1E2A3A;outline:none;
  transition:border-color 0.2s;width:100%;-webkit-appearance:none;
}
.oh-modal-input:focus,.oh-modal-select:focus{border-color:#4E8A3C;background:white}
.oh-modal-footer{padding:1.25rem 1.75rem;border-top:1px solid rgba(30,42,58,0.08);display:flex;justify-content:flex-end;gap:10px}
.oh-modal-cancel{background:transparent;border:1px solid rgba(30,42,58,0.15);border-radius:6px;padding:10px 20px;font-size:12px;font-family:'Syne',sans-serif;color:#6B7A8D;cursor:pointer;transition:all 0.2s}
.oh-modal-cancel:hover{color:#1E2A3A;border-color:#1E2A3A}
.oh-modal-save{background:#1E2A3A;color:white;border:none;border-radius:6px;padding:10px 24px;font-size:12px;font-family:'Syne',sans-serif;letter-spacing:0.5px;cursor:pointer;transition:background 0.2s}
.oh-modal-save:hover{background:#4E8A3C}
.oh-modal-save:disabled{opacity:0.5;cursor:not-allowed}

.oh-empty{padding:4rem 2rem;text-align:center;color:#6B7A8D;font-size:13px;font-weight:300}
.oh-loading{padding:3rem;text-align:center;color:#6B7A8D;font-size:13px}

@media (max-width: 768px) {
  .oh-filters{flex-direction:column;align-items:stretch}
  .oh-search{min-width:100%}
  .oh-table-wrap{overflow-x:auto}
  .oh-modal-grid{grid-template-columns:1fr}
}
`;

const SORT_ARROW = (field, sort) => {
  if (sort === field) return " ↑";
  if (sort === `-${field}`) return " ↓";
  return "";
};

const emptyFilters = {
  search: "", status: "", medical_records: "", size: "",
  date_from: "", date_to: "", billing_from: "", billing_to: "",
};

const EditModal = ({ order, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...order });
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const hasCollectionAmount = (value) => {
    const amount = Number(String(value || "").replace("$", "").replace(",", "").trim());
    return Number.isFinite(amount) && amount > 0;
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const api = axiosInstance();

      const payload = {
        ...form,
        compression_sock_status: hasCollectionAmount(form.collection)
          ? "Ready to Ship"
          : form.compression_sock_status,
      };

      await api.patch(`/orders/compressed-sock/${order.id}/update/`, payload);

      toast.success("Order updated.");
      onSaved();
      onClose();
    } catch {
      toast.error("Failed to update. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const F = ({ label, field, type = "text", children }) => (
    <div className="oh-modal-field">
      <label className="oh-modal-label">{label}</label>
      {children || (
        <input className="oh-modal-input" type={type}
          value={form[field] || ""} onChange={set(field)} />
      )}
    </div>
  );

  return (
    <motion.div className="oh-modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div className="oh-modal"
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.97 }}
        transition={{ duration: 0.24 }}
      >
        <div className="oh-modal-band" />
        <div className="oh-modal-header">
          <span className="oh-modal-title">Edit Order — {order.order_number}</span>
          <button className="oh-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="oh-modal-body">

          {/* Status fields */}
          <div className="oh-modal-grid">
            <div className="oh-modal-field">
              <label className="oh-modal-label">Status</label>
              <select className="oh-modal-select" value={form.compression_sock_status || ""} onChange={set("compression_sock_status")}>
                <option value="">Select status</option>
                {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="oh-modal-field">
              <label className="oh-modal-label">Medical Records</label>
              <select className="oh-modal-select" value={form.medical_records_status || ""} onChange={set("medical_records_status")}>
                <option value="">Select</option>
                {MEDICAL_RECORDS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <F label="Date Order Received" field="date_order_received" type="date" />
            <F label="Date Sent to Billing" field="date_sent_to_billing" type="date" />
          </div>

          {/* Patient */}
          <div className="oh-modal-grid">
            <F label="Patient Name" field="patient_name_or_id" />
            <F label="Patient DOB" field="patient_dob" type="date" />
            <F label="Phone #" field="phone_number" />
            <F label="Email" field="email_address" type="email" />
            <F label="Physician" field="physician" />
            <F label="Doctor Location" field="doctor_location" />
          </div>

          {/* Insurance */}
          <div className="oh-modal-grid">
            <F label="Primary Insurance" field="primary_insurance" />
            <F label="Primary Member ID" field="primary_member_id" />
            <F label="Ins #2" field="ins2" />
            <F label="Ins 2 ID" field="ins2_id" />
          </div>

          {/* Clinical */}
          <div className="oh-modal-grid">
            <F label="Dx Code 1" field="icd10_code" />
            <F label="Dx Code 2" field="dx_code_2" />
            <F label="Ankle (in)" field="ankle_measurement" />
            <F label="Calf (in)" field="calf_measurement" />
            <F label="Thigh (in)" field="thigh_measurement" />
            <F label="Length — Wraps" field="length_wraps" />
          </div>

          {/* Order */}
          <div className="oh-modal-grid">
            <F label="HCPC" field="hcpc" />
            <F label="Product" field="product_sku" />
            <div className="oh-modal-field">
              <label className="oh-modal-label">Size</label>
              <select className="oh-modal-select" value={form.size || ""} onChange={set("size")}>
                <option value="">Select</option>
                {SIZE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {/* Biller fields */}
          <div className="oh-modal-grid">
            <F label="Comments" field="comments" />
            <F label="Collection" field="collection" />
          </div>

        </div>
        <div className="oh-modal-footer">
          <button className="oh-modal-cancel" onClick={onClose}>Cancel</button>
          <button className="oh-modal-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const OrderHistory = () => {
  const [filters, setFilters] = useState(emptyFilters);
  const [debouncedFilters, setDebouncedFilters] = useState(emptyFilters);
  const [sort, setSort] = useState("-created_at");
  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const debounceRef = useRef(null);

  // Debounce filters
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [filters]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosInstance();
      const params = new URLSearchParams();
      Object.entries(debouncedFilters).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.append('sort', sort);
      params.append('page', page);
      params.append('page_size', 25);
      const res = await api.get(`/orders/compressed-sock/list/?${params.toString()}`);
      setData(res.data.data);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setStatusCounts(res.data.status_counts);
    } catch {
      toast.error("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters, sort, page]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchOrders();
    }, 60_000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const setFilter = (key) => (e) => setFilters(prev => ({ ...prev, [key]: e.target.value }));
  const clearFilters = () => { setFilters(emptyFilters); setPage(1); };

  const handleSort = (field) => {
    setSort(prev => prev === field ? `-${field}` : field);
    setPage(1);
  };

  const hasFilters = Object.values(filters).some(Boolean);

  // Count chips — top 4 most interesting statuses
  const chipStatuses = ["Ready to Ship", "Shipped", "DENIED-READ DETAILS", "Completed"];

  const fmt = (val) => val || "—";

  const hasCollectionAmount = (value) => {
    const amount = Number(String(value || "").replace("$", "").replace(",", "").trim());
    return Number.isFinite(amount) && amount > 0;
  };

  const handleReleaseToShipping = async (order) => {
    try {
      const api = axiosInstance();

      await api.patch(`/compressed-sock/${order.id}/trigger-shipping/`);

      toast.success("Order released to shipping.");
      fetchOrders();
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Failed to release order to shipping."
      );
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: ohStyles }} />
      <div className="oh-root">

        {/* STATUS CHIPS */}
        <div className="oh-chips">
          <div className="oh-chip">
            <span>Total</span><strong>{total}</strong>
          </div>
          {chipStatuses.map(s => statusCounts[s] ? (
            <div
              key={s}
              className={`oh-chip ${filters.status === s ? "highlight" : ""}`}
              style={{ cursor: "pointer" }}
              onClick={() => setFilters(prev => ({
                ...prev, status: prev.status === s ? "" : s
              }))}
            >
              <span>{s}</span><strong>{statusCounts[s]}</strong>
            </div>
          ) : null)}
        </div>

        {/* FILTER BAR */}
        <div className="oh-filters">
          <input
            className="oh-search"
            placeholder="Search patient, physician, HCPC, insurance…"
            value={filters.search}
            onChange={setFilter("search")}
          />

          <div className="oh-filter-group">
            <span className="oh-filter-label">Status</span>
            <select className="oh-filter-select" value={filters.status} onChange={setFilter("status")}>
              <option value="">All</option>
              {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          <div className="oh-filter-group">
            <span className="oh-filter-label">Med. Records</span>
            <select className="oh-filter-select" value={filters.medical_records} onChange={setFilter("medical_records")}>
              <option value="">All</option>
              {MEDICAL_RECORDS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          <div className="oh-filter-group">
            <span className="oh-filter-label">Size</span>
            <select className="oh-filter-select" value={filters.size} onChange={setFilter("size")}>
              <option value="">All</option>
              {SIZE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>



          <div className="oh-filter-group">
            <span className="oh-filter-label">Date Received</span>
            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              <input className="oh-filter-date" type="date" value={filters.date_from} onChange={setFilter("date_from")} />
              <span style={{ color: "#6B7A8D", fontSize: "11px" }}>→</span>
              <input className="oh-filter-date" type="date" value={filters.date_to} onChange={setFilter("date_to")} />
            </div>
          </div>

          <div className="oh-filter-group">
            <span className="oh-filter-label">Date Billing</span>
            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              <input className="oh-filter-date" type="date" value={filters.billing_from} onChange={setFilter("billing_from")} />
              <span style={{ color: "#6B7A8D", fontSize: "11px" }}>→</span>
              <input className="oh-filter-date" type="date" value={filters.billing_to} onChange={setFilter("billing_to")} />
            </div>
          </div>

          {hasFilters && (
            <button className="oh-clear-btn" onClick={clearFilters}>✕ Clear</button>
          )}
        </div>

        {/* TABLE */}
        <div className="oh-table-wrap">
          {loading ? (
            <div className="oh-loading">Loading orders…</div>
          ) : data.length === 0 ? (
            <div className="oh-empty">No orders found. Try adjusting your filters.</div>
          ) : (
            <>
              <table className="oh-table">
                <thead>
                  <tr>
                    {[
                      ["order_number", "Order #"],
                      ["patient_name_or_id", "Patient"],
                      [null, "Physician"],
                      ["compression_sock_status", "Status"],
                      [null, "Med. Records"],
                      ["size", "Size"],
                      [null, "HCPC"],
                      ["date_order_received", "Date Received"],
                      ["date_sent_to_billing", "Date Billing"],
                      [null, "Comments"],
                      [null, "Collection"],
                      [null, "Actions"],
                    ].map(([field, label]) => (
                      <th
                        key={label}
                        className={field && (sort === field || sort === `-${field}`) ? "sorted" : ""}
                        onClick={() => field && handleSort(field)}
                      >
                        {label}{field && SORT_ARROW(field, sort)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map(order => (
                    <tr key={order.id}>
                      <td><strong>{order.order_number}</strong></td>
                      <td>{fmt(order.patient_name_or_id)}</td>
                      <td className="muted">{fmt(order.physician)}</td>
                      <td><StatusBadge value={order.compression_sock_status} /></td>
                      <td className="muted">{fmt(order.medical_records_status)}</td>
                      <td>{fmt(order.size)}</td>
                      <td className="muted">{fmt(order.hcpc)}</td>
                      <td className="muted">{order.date_order_received || "—"}</td>
                      <td className="muted">{order.date_sent_to_billing || "—"}</td>
                      <td className="muted truncate" title={order.comments}>{fmt(order.comments)}</td>
                      <td className="muted truncate" title={order.collection}>{fmt(order.collection)}</td>
                      <td>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button className="oh-edit-btn" onClick={() => setEditOrder(order)}>
                            Edit
                          </button>

                          <button
                            className="oh-edit-btn"
                            disabled={
                              !hasCollectionAmount(order.collection) ||
                              order.compression_released_to_shipping
                            }
                            onClick={() => handleReleaseToShipping(order)}
                            title={
                              !hasCollectionAmount(order.collection)
                                ? "Enter collection amount first"
                                : "Release to shipping"
                            }
                          >
                            {order.compression_released_to_shipping
                              ? "Released"
                              : "Release to Shipping"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* PAGINATION */}
              <div className="oh-pagination">
                <span className="oh-page-info">
                  {total} orders · Page {page} of {totalPages}
                </span>
                <div className="oh-page-btns">
                  <button className="oh-page-btn" disabled={page === 1} onClick={() => setPage(1)}>«</button>
                  <button className="oh-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                    return p <= totalPages ? (
                      <button key={p} className={`oh-page-btn ${p === page ? "active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                    ) : null;
                  })}
                  <button className="oh-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                  <button className="oh-page-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
                </div>
              </div>
            </>
          )}
        </div>

      </div>

      <AnimatePresence>
        {editOrder && (
          <EditModal
            key={editOrder.id}
            order={editOrder}
            onClose={() => setEditOrder(null)}
            onSaved={fetchOrders}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default OrderHistory;